import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import '../../../core/styles/modal/CoreModal.css';
import '../../../core/styles/boat/BoatLeadModal.css';
import ShareExperienceForm from '../../../core/forms/contact/ShareExperienceForm';
import ShareExperienceSuccessModal from './ShareExperienceSuccessModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { validateShareExperienceForm } from '../../../core/validations/ShareExperienceValidation';
import BlogExperienceService from '../../../core/services/BlogExperienceService';

const ShareExperienceModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    message: '',
    images: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const maxImages = 10;
    
    setFormData((prevData) => {
      const currentImages = prevData.images || [];
      const totalImages = currentImages.length + newFiles.length;
      
      if (totalImages > maxImages) {
        const allowedNewFiles = newFiles.slice(0, maxImages - currentImages.length);
        return {
          ...prevData,
          images: [...currentImages, ...allowedNewFiles],
        };
      }
      
      return {
        ...prevData,
        images: [...currentImages, ...newFiles],
      };
    });
    
    // Reset the input so the same files can be selected again if needed
    e.target.value = '';
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData((prevData) => ({
      ...prevData,
      images: prevData.images.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleResetAndClose = () => {
    setFormData({
      fullName: '',
      email: '',
      message: '',
      images: []
    });
    setErrors({});
    setShowSuccessModal(false);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});
    const validationErrors = validateShareExperienceForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Enviar la experiencia usando el servicio
      const response = await BlogExperienceService.submitExperience(formData);
      
      // Mostrar popup de éxito
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error sharing experience:', error);
      setErrors({ submit: error.message || 'Error sending your experience. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  return createPortal(
    <div className="core__modal" onClick={handleResetAndClose}>
      <div className="core__modal-content" onClick={stopPropagation}>
        <div className="core__modal-header">
          <h3>Share Your Experience</h3>
          <button className="core__modal-close" onClick={handleResetAndClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        <div className="core__modal-body">
          <p className="core__modal-intro-text">
            Tell us about your boating adventure! Share your story, tips, or memorable moments with our community.
          </p>
          <ShareExperienceForm 
            formData={formData} 
            errors={errors} 
            handleChange={handleChange}
            handleImageChange={handleImageChange}
            handleRemoveImage={handleRemoveImage}
          />
          {errors.submit && <span className="error-message">{errors.submit}</span>}
          <div className="privacy-assurance">
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>We won't share your data.</span>
          </div>
          <div className="core__modal-buttons">
            <button className="button" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Sharing...' : 'Share Experience'}
            </button>
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <ShareExperienceSuccessModal 
          onClose={handleResetAndClose}
        />
      )}
    </div>,
    document.getElementById('core-app')
  );
};

export default ShareExperienceModal;
